"use client"

import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { FormItem, FormLabel, FormDescription, FormMessage, FormField } from "@/components/ui/form"
import type { FormElement } from "./types"
import type { UseFormReturn } from "react-hook-form"

interface FormInputProps {
  element: FormElement
  form: UseFormReturn<any>
}

export function FormInput({ element, form }: FormInputProps) {
  const inputType = element.attributes?.type || "text"

  if (inputType === "checkbox") {
    return (
      <FormField
        control={form.control}
        name={element.name!}
        rules={{
          required: element.validation?.required ? `${element.ui?.label || element.name} is required` : false,
        }}
        render={({ field }) => (
          <FormItem className="flex items-center gap-2 space-y-0">
            <Checkbox checked={field.value} onCheckedChange={field.onChange} id={`form-${element.name}`} />
            {element.ui?.label && (
              <FormLabel htmlFor={`form-${element.name}`} className="font-normal cursor-pointer">
                {element.ui.label}
              </FormLabel>
            )}
            {element.ui?.hint && <FormDescription>{element.ui.hint}</FormDescription>}
            <FormMessage />
          </FormItem>
        )}
      />
    )
  }

  return (
    <FormField
      control={form.control}
      name={element.name!}
      rules={{
        required: element.validation?.required ? `${element.ui?.label || element.name} is required` : false,
        minLength: element.validation?.minLength
          ? { value: element.validation.minLength, message: `Minimum length is ${element.validation.minLength}` }
          : undefined,
        maxLength: element.validation?.maxLength
          ? { value: element.validation.maxLength, message: `Maximum length is ${element.validation.maxLength}` }
          : undefined,
        min:
          element.validation?.min != null
            ? { value: element.validation.min, message: `Minimum value is ${element.validation.min}` }
            : undefined,
        max:
          element.validation?.max != null
            ? { value: element.validation.max, message: `Maximum value is ${element.validation.max}` }
            : undefined,
        pattern: element.validation?.pattern
          ? { value: new RegExp(element.validation.pattern), message: "Invalid format" }
          : undefined,
      }}
      render={({ field }) => (
        <FormItem>
          {element.ui?.label && <FormLabel htmlFor={`form-${element.name}`}>{element.ui.label}</FormLabel>}
          <Input
            id={`form-${element.name}`}
            type={inputType}
            placeholder={element.ui?.placeholder}
            name={field.name}
            value={field.value || ""}
            onChange={field.onChange}
            onBlur={field.onBlur}
            ref={field.ref}
          />
          {element.ui?.hint && <FormDescription>{element.ui.hint}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

